import { useMutation } from "@tanstack/react-query";
import { Entity } from "@common/types/entity";
import { RampConnection } from "@common/types/rampConnection";
import { FinancialConnection } from "@common/types/financialConnection";
import { createRampConnection, fixRampConnection } from "@lib/ramp";
import {
    addFinancialConnectionInQueryData,
    clearConnectionErrorInQueryData,
} from "@queries/entitiesAccounts";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { invalidateAccountRelatedQueries } from "@api/account.api";
import { useConnectAccount } from "@components/connectAccount/ConnectAccount.context";
import { useConnectTarget } from "@hooks/useConnectTarget";
import { queryClient, rampConnectionsQueryKey } from "../queryClient";

interface RampConnectionCreationPayload {
    code: string;
    defaultEntity?: Entity;
}
interface UseRampConnectionCreationMutationProps {
    onSuccess?: () => void;
    toastMessage?: string;
}
export function useRampConnectionCreationMutation({
    onSuccess,
    toastMessage = `Ramp account connected`,
}: UseRampConnectionCreationMutationProps) {
    const { toast } = useToaster();
    const { updateConnectAccountState } = useConnectAccount();
    const { workspaceId, refreshConnections } = useConnectTarget();

    return useMutation({
        mutationFn: ({ code, defaultEntity }: RampConnectionCreationPayload) =>
            createRampConnection({ code, defaultEntityId: defaultEntity?.id }),
        onSuccess: async (connection) => {
            queryClient.setQueryData(rampConnectionsQueryKey, [
                ...(queryClient.getQueryData<RampConnection[]>(
                    rampConnectionsQueryKey,
                ) ?? []),
                connection,
            ]);
            addFinancialConnectionInQueryData(queryClient, connection);
            invalidateAccountRelatedQueries({ workspaceId });
            if (!connection.accounts[0].financialAccount?.entity) {
                updateConnectAccountState({
                    isConnecting: false,
                    connectionSaved: true,
                    connection,
                    foreign: [],
                });
            } else {
                toast(toastMessage);
            }
            await refreshConnections({
                provider: "ramp",
                connectionId: String(connection.id),
            });
            onSuccess?.();
        },
    });
}

interface FixRampConnectionPayload {
    connection: FinancialConnection;
    code: string;
}
export function useFixRampConnectionsMutation(onSuccess?: () => unknown) {
    return useMutation({
        mutationFn: (payload: FixRampConnectionPayload) =>
            fixRampConnection(payload.connection.id, { code: payload.code }),
        onSuccess: (connected) => {
            if (!connected) {
                return;
            }
            clearConnectionErrorInQueryData(queryClient, connected.id);
            onSuccess?.();
            queryClient.setQueryData<FinancialConnection[] | undefined>(
                rampConnectionsQueryKey,
                (connections) =>
                    connections?.map((c) =>
                        c.id === connected.id
                            ? {
                                  ...c,
                                  accounts: connected.accounts,
                                  error: null,
                              }
                            : c,
                    ) ?? [],
            );
        },
    });
}
